import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL!,
  process.env.REACT_APP_SUPABASE_ANON_KEY!
);

async function adminFetch(path: string, options: RequestInit = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || '';
  const res = await fetch(path, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  return res.json();
}

interface Participant {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  organization?: string;
  created_at: string;
}

interface Simulation {
  id: string;
  name: string;
  description: string;
  scenario: string;
  is_active: boolean;
  ai_persona_config: any;
  created_at: string;
}

interface Persona {
  key: string;
  name: string;
  character: string;
  role: string;
}

interface Assignment {
  id: string;
  participant_id: string;
  simulation_id: string;
  status: string;
  assigned_at: string;
  due_date?: string;
  participant?: { first_name: string; last_name: string; email: string };
  simulation?: { name: string };
}

interface AdminPanelProps {
  user?: any;
}

const AdminPanel: React.FC<AdminPanelProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'participants' | 'simulations' | 'analytics' | 'assignments'>('participants');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [simulations, setSimulations] = useState<Simulation[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteStatus, setInviteStatus] = useState('');

  const [newParticipant, setNewParticipant] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'participant',
    organization: '',
  });

  const [newSimulation, setNewSimulation] = useState({
    name: '',
    description: '',
    scenario: '',
    personaKey: '',
    isActive: true,
  });

  const [briefText, setBriefText] = useState('');
  const [briefName, setBriefName] = useState('');
  const [briefStatus, setBriefStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [briefLoading, setBriefLoading] = useState(false);

  const [newAssignment, setNewAssignment] = useState({
    participantId: '',
    simulationId: '',
    dueDate: '',
  });

  useEffect(() => {
    loadParticipants();
    loadSimulations();
    loadPersonas();
    loadAssignments();
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'participants') loadParticipants();
    else if (activeTab === 'simulations') { loadSimulations(); loadPersonas(); }
    else if (activeTab === 'assignments') loadAssignments();
    else if (activeTab === 'analytics') loadAnalytics();
  }, [activeTab]);

  const loadParticipants = async () => {
    const r = await adminFetch('/api/admin/participants');
    setParticipants(r.data || []);
  };

  const loadSimulations = async () => {
    const r = await adminFetch('/api/admin/simulations');
    setSimulations(r.data || []);
  };

  const loadPersonas = async () => {
    try {
      const res = await fetch('/api/personas');
      const data = await res.json();
      setPersonas(data || []);
    } catch (e) {
      setPersonas([]);
    }
  };

  const loadAssignments = async () => {
    const r = await adminFetch('/api/admin/assignments');
    setAssignments(r.data || []);
  };

  const loadAnalytics = async () => {
    const r = await adminFetch('/api/admin/analytics');
    if (r.success) setAnalytics(r.data);
  };

  const sendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setInviteStatus('');
    const r = await adminFetch('/api/admin/invite', {
      method: 'POST',
      body: JSON.stringify({ email: inviteEmail }),
    });
    if (r.success) {
      setInviteStatus(`Magic link sent to ${inviteEmail}`);
      setInviteEmail('');
    } else {
      setError(r.error || 'Failed to send invite');
    }
    setLoading(false);
  };

  const addParticipant = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newParticipant.email,
        password: Math.random().toString(36).slice(-10) + 'A1!',
        options: {
          data: {
            role: newParticipant.role,
            first_name: newParticipant.firstName,
            last_name: newParticipant.lastName,
          }
        }
      });
      if (authError) throw authError;

      const { error: participantError } = await supabase.from('participants').insert({
        user_id: authData.user?.id,
        email: newParticipant.email,
        first_name: newParticipant.firstName,
        last_name: newParticipant.lastName,
        role: newParticipant.role,
        organization: newParticipant.organization || null,
      });
      if (participantError) throw participantError;

      setNewParticipant({ firstName: '', lastName: '', email: '', role: 'participant', organization: '' });
      await loadParticipants();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateFromBrief = async (e: React.FormEvent) => {
    e.preventDefault();
    setBriefStatus(null);
    setBriefLoading(true);
    try {
      const r = await adminFetch('/api/admin/generate-scenario', {
        method: 'POST',
        body: JSON.stringify({ scenarioBrief: briefText, simulationName: briefName || undefined }),
      });
      if (!r.success) throw new Error(r.error || 'Generation failed');
      setBriefStatus({ type: 'success', msg: `Created: "${r.simulationName}" — ${r.personaName}, ${r.personaRole}` });
      setBriefText('');
      setBriefName('');
      await loadSimulations();
    } catch (err: any) {
      setBriefStatus({ type: 'error', msg: err.message });
    } finally {
      setBriefLoading(false);
    }
  };

  const addSimulation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const selectedPersona = personas.find(p => p.key === newSimulation.personaKey);
      const { error } = await supabase.from('simulations').insert({
        name: newSimulation.name,
        description: newSimulation.description,
        scenario: newSimulation.scenario,
        is_active: newSimulation.isActive,
        evaluation_criteria: {
          certification_minimum: {
            cof_elements: ['clinical', 'operational', 'financial'],
            open_ended_questions: true,
            price_objection_handling: 'configuration_pivot',
          },
          best_practice: {
            quantification: 'dollar_figure',
            collaborative_approach: true,
            strategic_questioning: true,
          },
        },
        ai_persona_config: {
          persona_key: newSimulation.personaKey,
          name: selectedPersona?.character || '',
          role: selectedPersona?.role || '',
        },
      });
      if (error) throw error;
      setNewSimulation({ name: '', description: '', scenario: '', personaKey: '', isActive: true });
      await loadSimulations();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const assignSimulation = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const r = await adminFetch('/api/admin/assignments', {
        method: 'POST',
        body: JSON.stringify({
          participantId: newAssignment.participantId,
          simulationId: newAssignment.simulationId,
          dueDate: newAssignment.dueDate || null,
        }),
      });
      if (!r.success) throw new Error(r.error || 'Failed to create assignment');
      setNewAssignment({ participantId: '', simulationId: '', dueDate: '' });
      await loadAssignments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const revokeAssignment = async (assignmentId: string) => {
    if (!window.confirm('Revoke this assignment?')) return;
    const r = await adminFetch(`/api/admin/assignments/${assignmentId}`, { method: 'DELETE' });
    if (r.success) await loadAssignments();
    else setError(r.error || 'Failed to revoke assignment');
  };

  const statusColor: Record<string, string> = {
    assigned: '#3b82f6',
    in_progress: '#f59e0b',
    completed: '#10b981',
    overdue: '#ef4444',
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage participants, simulations, assignments, and analytics</p>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>
          {error}
          <button onClick={() => setError(null)} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>✕</button>
        </div>
      )}

      <div className="admin-tabs">
        <button className={`tab ${activeTab === 'participants' ? 'active' : ''}`} onClick={() => setActiveTab('participants')}>Participants</button>
        <button className={`tab ${activeTab === 'simulations' ? 'active' : ''}`} onClick={() => setActiveTab('simulations')}>Simulations</button>
        <button className={`tab ${activeTab === 'assignments' ? 'active' : ''}`} onClick={() => setActiveTab('assignments')}>
          Assignments {assignments.length > 0 && <span style={{ background: '#3b82f6', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, marginLeft: 6 }}>{assignments.length}</span>}
        </button>
        <button className={`tab ${activeTab === 'analytics' ? 'active' : ''}`} onClick={() => setActiveTab('analytics')}>Analytics</button>
      </div>

      <div className="admin-content">

        {activeTab === 'participants' && (
          <div className="participants-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2>Manage Participants</h2>
              <button className="btn btn-primary" onClick={() => document.getElementById('add-participant-form')?.scrollIntoView({ behavior: 'smooth' })}>+ Add Participant</button>
            </div>

            {/* Invite by Email */}
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: '18px 20px', marginBottom: 28 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0369a1' }}>Send Practice Access Link</h3>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>Send a one-click magic link so the recipient can access the platform without setting a password.</p>
              <form onSubmit={sendInvite} style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  <input
                    type="email"
                    placeholder="recipient@company.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <button type="submit" disabled={loading || !inviteEmail.trim()} className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                  Send Link
                </button>
              </form>
              {inviteStatus && (
                <p style={{ marginTop: 10, fontSize: 13, color: '#15803d', fontWeight: 600 }}>{inviteStatus}</p>
              )}
            </div>

            <div className="participants-grid">
              {participants.map((p) => (
                <div key={p.id} className="participant-card">
                  <div className="participant-info">
                    <h3>{p.first_name} {p.last_name}</h3>
                    <p className="participant-email">{p.email}</p>
                    <div className="participant-meta">
                      <span className="role-badge">{p.role}</span>
                      {p.organization && <span className="organization">{p.organization}</span>}
                    </div>
                    <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>Joined: {new Date(p.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              ))}
              {participants.length === 0 && <p style={{ color: '#6b7280' }}>No participants yet.</p>}
            </div>
            <div id="add-participant-form" className="add-form">
              <h3>Add New Participant</h3>
              <form onSubmit={addParticipant}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <input type="text" placeholder="First Name" value={newParticipant.firstName} onChange={(e) => setNewParticipant({ ...newParticipant, firstName: e.target.value })} required />
                  <input type="text" placeholder="Last Name" value={newParticipant.lastName} onChange={(e) => setNewParticipant({ ...newParticipant, lastName: e.target.value })} required />
                </div>
                <input type="email" placeholder="Email Address" value={newParticipant.email} onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })} required />
                <input type="text" placeholder="Organization (optional)" value={newParticipant.organization} onChange={(e) => setNewParticipant({ ...newParticipant, organization: e.target.value })} />
                <select value={newParticipant.role} onChange={(e) => setNewParticipant({ ...newParticipant, role: e.target.value })}>
                  <option value="participant">Participant</option>
                  <option value="trainer">Trainer</option>
                  <option value="admin">Admin</option>
                </select>
                <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Adding...' : 'Add Participant'}</button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'simulations' && (
          <div className="simulations-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2>Manage Simulations</h2>
              <button className="btn btn-primary" onClick={() => document.getElementById('add-simulation-form')?.scrollIntoView({ behavior: 'smooth' })}>+ Create Simulation</button>
            </div>
            <div className="simulations-grid">
              {simulations.map((sim) => (
                <div key={sim.id} className="simulation-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3>{sim.name}</h3>
                    <span className={`status-badge ${sim.is_active ? 'active' : 'inactive'}`}>{sim.is_active ? 'Active' : 'Inactive'}</span>
                  </div>
                  <p className="simulation-description">{sim.description}</p>
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>Created: {new Date(sim.created_at).toLocaleDateString()}</p>
                </div>
              ))}
              {simulations.length === 0 && <p style={{ color: '#6b7280' }}>No simulations yet.</p>}
            </div>
            {/* Generate from Brief */}
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '18px 20px', marginBottom: 28 }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#15803d' }}>Generate Scenario from Brief</h3>
              <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 14px' }}>
                Paste a manager scenario brief — account type, persona role, objection focus, learning goal. GPT will generate the full persona and simulation automatically.
              </p>
              <form onSubmit={generateFromBrief}>
                <input
                  type="text"
                  placeholder="Simulation name (optional — GPT will generate one)"
                  value={briefName}
                  onChange={(e) => setBriefName(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
                <textarea
                  placeholder="Paste your scenario brief here...&#10;&#10;Example: Create a scenario for a 300-bed community hospital CFO who is skeptical about capital equipment ROI. The sales rep should practice uncovering throughput bottlenecks in the cardiac cath lab, quantifying lost procedure revenue, and pivoting from a price objection to a phased-payment solution."
                  value={briefText}
                  onChange={(e) => setBriefText(e.target.value)}
                  rows={6}
                  required
                  style={{ marginBottom: 10 }}
                />
                <button type="submit" disabled={briefLoading || briefText.trim().length < 30} className="btn btn-primary">
                  {briefLoading ? 'Generating...' : 'Generate Simulation'}
                </button>
              </form>
              {briefStatus && (
                <p style={{ marginTop: 12, fontSize: 13, fontWeight: 600, color: briefStatus.type === 'success' ? '#15803d' : '#dc2626' }}>
                  {briefStatus.type === 'success' ? '✓ ' : '✕ '}{briefStatus.msg}
                </p>
              )}
            </div>

            <div id="add-simulation-form" className="add-form">
              <h3>Create New Simulation</h3>
              <form onSubmit={addSimulation}>
                <input type="text" placeholder="Simulation Name" value={newSimulation.name} onChange={(e) => setNewSimulation({ ...newSimulation, name: e.target.value })} required />
                <input type="text" placeholder="Description" value={newSimulation.description} onChange={(e) => setNewSimulation({ ...newSimulation, description: e.target.value })} required />
                <div className="form-group">
                  <label htmlFor="persona-select">AI Persona</label>
                  <select id="persona-select" value={newSimulation.personaKey} onChange={(e) => setNewSimulation({ ...newSimulation, personaKey: e.target.value })}>
                    {personas.length > 0
                      ? personas.map((p) => <option key={p.key} value={p.key}>{p.name} — {p.character} ({p.role})</option>)
                      : <option value="" disabled>Loading personas...</option>}
                  </select>
                </div>
                <textarea placeholder="Scenario Description" value={newSimulation.scenario} onChange={(e) => setNewSimulation({ ...newSimulation, scenario: e.target.value })} rows={4} required />
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={newSimulation.isActive} onChange={(e) => setNewSimulation({ ...newSimulation, isActive: e.target.checked })} />
                  Active
                </label>
                <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Creating...' : 'Create Simulation'}</button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'assignments' && (
          <div className="assignments-section">
            <div style={{ marginBottom: 24 }}>
              <h2>Assign Simulations</h2>
              <p style={{ color: '#6b7280', marginTop: 4 }}>Give participants access to specific simulations. They will see their assigned simulations when they log in.</p>
            </div>
            <div className="add-form" style={{ marginBottom: 32 }}>
              <h3>New Assignment</h3>
              <form onSubmit={assignSimulation}>
                <div className="form-group">
                  <label>Participant</label>
                  <select value={newAssignment.participantId} onChange={(e) => setNewAssignment({ ...newAssignment, participantId: e.target.value })} required>
                    <option value="">— Select a participant —</option>
                    {participants.map((p) => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name} — {p.email}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Simulation</label>
                  <select value={newAssignment.simulationId} onChange={(e) => setNewAssignment({ ...newAssignment, simulationId: e.target.value })} required>
                    <option value="">— Select a simulation —</option>
                    {simulations.filter(s => s.is_active).map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Due Date (optional)</label>
                  <input type="date" value={newAssignment.dueDate} onChange={(e) => setNewAssignment({ ...newAssignment, dueDate: e.target.value })} />
                </div>
                <button type="submit" disabled={loading || !newAssignment.participantId || !newAssignment.simulationId} className="btn btn-primary">
                  {loading ? 'Assigning...' : 'Assign Simulation'}
                </button>
              </form>
            </div>
            <h3 style={{ marginBottom: 16 }}>Current Assignments ({assignments.length})</h3>
            {assignments.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No assignments yet. Use the form above to assign simulations to participants.</p>
            ) : (
              <div className="assignments-list">
                {assignments.map((a) => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, padding: '14px 18px', marginBottom: 10 }}>
                    <div>
                      <p style={{ fontWeight: 600, marginBottom: 2 }}>{a.participant?.first_name} {a.participant?.last_name}</p>
                      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>{a.participant?.email}</p>
                      <p style={{ fontSize: 13 }}>📋 {a.simulation?.name}</p>
                      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
                        <span>Assigned: {new Date(a.assigned_at).toLocaleDateString()}</span>
                        {a.due_date && <span>Due: {new Date(a.due_date).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
                      <span style={{ background: statusColor[a.status] || '#6b7280', color: '#fff', borderRadius: 12, padding: '3px 10px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize' as const }}>
                        {a.status.replace('_', ' ')}
                      </span>
                      <button onClick={() => revokeAssignment(a.id)} style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                        Revoke
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="analytics-section">
            <h2>Utilization Dashboard</h2>

            {/* Dev Tools */}
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, padding: '14px 18px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: 14, color: '#92400e' }}>Intro Counter</p>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: '#b45309' }}>Resets the opening intro so it fires again on your next session (for testing).</p>
              </div>
              <button
                onClick={() => {
                  localStorage.removeItem(`sim_intro_count_${user?.id}`);
                  alert('Intro counter reset. Next simulation will play the opening again.');
                }}
                style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' as const }}
              >
                Reset My Intro
              </button>
            </div>
            {!analytics ? (
              <p>Loading...</p>
            ) : (
              <>
                <div className="analytics-grid">
                  <div className="metric-card"><h3>Total Sessions</h3><div className="metric-value">{analytics.totalSessions}</div></div>
                  <div className="metric-card"><h3>This Week</h3><div className="metric-value">{analytics.sessionsThisWeek}</div></div>
                  <div className="metric-card"><h3>Active Practitioners</h3><div className="metric-value">{analytics.activeParticipants}</div></div>
                  <div className="metric-card"><h3>Cert Rate</h3><div className="metric-value">{analytics.certRate}%</div></div>
                </div>

                <h3 style={{ marginTop: 32, marginBottom: 12 }}>Top Scenarios Used</h3>
                {analytics.topPersonas.map((p: any) => (
                  <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 16px', background: '#f9fafb', borderRadius: 8, marginBottom: 8, fontSize: 14 }}>
                    <span>{p.name}</span>
                    <span style={{ fontWeight: 700, color: '#3b82f6' }}>{p.count} sessions</span>
                  </div>
                ))}

                {analytics.zeroSessionParticipants.length > 0 && (
                  <>
                    <h3 style={{ marginTop: 32, marginBottom: 12, color: '#dc2626' }}>Not Yet Active ({analytics.zeroSessionParticipants.length})</h3>
                    {analytics.zeroSessionParticipants.map((p: any) => (
                      <div key={p.email} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 16px', background: '#fff7ed', borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                        <span>{p.name}</span>
                        <span style={{ color: '#9ca3af' }}>{p.email}</span>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default AdminPanel;
